/**
 * Handles all the pinned views for the current user and for the current event.
 * This will also store the pinned views on the server so the user can access them on other devices.
 *
 */

import { useEventStoreRef, useUserStoreRef } from '@/stores';
import { computed, onMounted, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { PinnedViewParams } from './PinnedViewParams';
import { colorMap, colorMapSecondary } from '@/utilities/colorUtils';
import { useStoredSettings } from '../settings';
import { PinnedViewsSettings } from './pinnedViewsSettings';
import { Router } from '@/objects/Router';
import type { Route } from '@/objects/Route';
import { StackedIcon, type IconName } from '@/fontawesome';

export const usePinnedViews = defineStore('pinnedviews', () => {
    // A reference to the user so we can check if the user is a super admin.
    const { user } = useUserStoreRef();

    // A reference to the eventID so we can set the eventID for the device status.
    const { eventID } = useEventStoreRef();

    // We need a handle to the stored settings so we can load and save the pinned views.
    const settings = useStoredSettings();

    // A list of all the colors that are assigned to the pinned views.
    // This could have been checked on the pinnedViews objects, but this is easier
    const assignedColors: string[] = [];

    // The pinned views map. This will contain all the pinned views.
    const pinnedviews = ref<Map<string, PinnedViewParams>>(new Map());

    // We expose the current clicked view so we can listen for changes
    const currentClickedView = ref<PinnedViewParams | null>(null);

    // Watch the eventID and clear the pinned views for the old eventID
    watch(eventID, async () => await loadSettings());

    /**
     * Assign a color to the view either from lists of predefined colors or generate a random color if all colors are used.
     * @param view
     */
    const assignColor = (view: PinnedViewParams) => {
        let color = colorMap.find((c) => !assignedColors.includes(c));

        if (!color) {
            color = colorMapSecondary.find((c) => !assignedColors.includes(c));
        }

        if (!color) {
            // Generate radom color
            color = colorMapSecondary[Math.floor(Math.random() * colorMapSecondary.length)];
        }
        assignedColors.push(color!);
        view.color = color;
    };

    /**
     * Pin a view to the pinned views.
     * This will add the view to the pinned views and assign a color to it. And display it in the pinned view container
     * @param title The title presented in the pinned view
     * @param route The route to navigate to when the view is clicked
     * @param icon The icon presented in the pinned view
     * @returns
     */
    const pinView = (title: string, route: Route, icon?: IconName | StackedIcon) => {
        const view = new PinnedViewParams().CreateFromRoute(title, route);

        if (icon) {
            view.icon = icon;
        }

        if (pinnedviews.value.has(view.getUniqueID())) {
            setViewActive(view, true);
            saveSettings();
            return;
        }

        // Assign a color to the view
        assignColor(view);

        pinnedviews.value.set(view.getUniqueID(), view);

        saveSettings();
    };

    /**
     * Set the view to active or inactive
     * @param view
     * @param active
     */
    const setViewActive = (view: PinnedViewParams, active: boolean) => {
        if (pinnedviews.value.has(view.getUniqueID())) {
            const _view = pinnedviews.value.get(view.getUniqueID());
            if (_view !== undefined) {
                _view.active = active;
            }
        }
    };

    /**
     * Unpin a view from the pinned views.
     * @param view
     */
    const unPinView = (view: PinnedViewParams | string, hash?: string) => {
        if (view instanceof PinnedViewParams) {
            // Do nothing
        } else {
            view = new PinnedViewParams().CreateFromRoute(view, Router.CurrentRoute);
        }

        if (hash) {
            view.hash = hash;
        }

        setViewActive(view, false);

        saveSettings();
    };

    /**
     * Helper method to create a view from a title and the current route
     * @param title
     * @returns
     */
    const createView = (title: string) => new PinnedViewParams().CreateFromRoute(title, Router.CurrentRoute);

    /**
     * Get the active pinned views
     */
    const activePinnedViews = computed<Map<string, PinnedViewParams>>(() => {
        return new Map(Array.from(pinnedviews.value.entries()).filter((v) => v[1].active));
    });

    /**
     * Save the settings to the server
     */
    const saveSettings = async () => {
        const settingsToSave = new PinnedViewsSettings();
        settingsToSave.pinnedViews = Array.from(pinnedviews.value.values());

        await settings.SaveSettings(PinnedViewsSettings, settingsToSave, eventID.value, user.value?.ID, null);
    };

    /**
     * Load the settings from the server
     */
    const loadSettings = async () => {
        pinnedviews.value.clear();

        const storedSettings =
            (await settings.GetSettings<PinnedViewsSettings>(PinnedViewsSettings, eventID.value, user.value?.ID, null)) ??
            new PinnedViewsSettings();

        storedSettings.pinnedViews.forEach((view) => {
            const _view = new PinnedViewParams(view);
            assignedColors.push(_view.color!);
            pinnedviews.value.set(_view.getUniqueID(), _view);
        });

        console.debug('Loaded pinned views', pinnedviews.value, eventID.value);
    };

    /**
     * Setup the composable and get the router.
     */
    onMounted(async () => {
        await loadSettings();
    });

    return {
        /**
         * Pin a view to the pinned views
         * @param title The title presented in the pinned view
         * @param route The route to navigate to when the view is clicked
         * @param icon The icon presented in the pinned view
         */
        PinView: pinView,

        /**
         * Unpin a view from the pinned views
         * @param view The view to unpin
         * @param hash The hash of the view
         * @returns
         */
        UnpinView: unPinView,

        /**
         * A list of all the pinned views
         * @returns
         */
        PinnedViews: pinnedviews,

        /**
         * A list of all the active pinned views
         */
        ActivePinnedViews: activePinnedViews,

        // CreateView: createView,

        /**
         * The last view that was clicked
         */
        CurrentClickedView: currentClickedView,
    };
});

/**
 *
 * @returns a ref version of the usePinnedViews store.
 */
export const usePinnedViewsRef = () => storeToRefs(usePinnedViews());
